import React, { forwardRef, useEffect, useState } from "react"
import { Container } from "react-bootstrap"
import { useSelector } from "react-redux"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import moment from "moment"
import {
  findIndex, get, isEmpty, orderBy
} from "lodash"

import { getAllJobServices } from "requesters/jobServiceRequester"
import {
  LIVE_JOB_STATUS_OPTIONS,
  RECEPTION_MANAGER
} from "utils/constants"

import AnchorButton from "components/AnchorButton"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import SearchInput from "components/SearchInput"
import { refreshPageTableZoom } from "utils/tableZoomEnhancer"
import JobServicesTable from "./JobServicesTable"

import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../reducers"

const DateFilterButton = forwardRef(({ value, onClick, filterLabel, rangeLabel }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-light btn-lg rounded-pill"
    onClick={onClick}>
    {`${filterLabel}: ${rangeLabel || value || "All"}`}
  </button>
))

const formatRangeLabel = (start, end) => {
  if (!start) return ""
  const startLabel = moment(start, "YYYY-MM-DD").format("DD/MM/YYYY")
  if (!end || start === end) return startLabel
  return `${startLabel} - ${moment(end, "YYYY-MM-DD").format("DD/MM/YYYY")}`
}

export default function JobServices() {
  const baseUrl = useSelector(getBaseUrl)
  const currentRole = useSelector(getCurrentRole)
  const currentUser = useSelector(getCurrentUser)
  const currentTeamRole = currentUser.attributes.current_team_role
  const [requesting, setRequesting] = useState(false)
  const [jobServiceList, setJobServiceList] = useState([])

  const params = (new URL(document.location)).searchParams;
  const filter = params.get("filter");
  const job_service_id = params.get("job_service_id");

  const initialFilters = {
    by_keyword: undefined,
    by_status: filter === "ready_to_send" ? "ready_to_send" : undefined,
    field_work_start: undefined,
    field_work_end: undefined,
    due_date_start: undefined,
    due_date_end: undefined,
    job_pack_not_uploaded: (filter === "job_pack_not_uploaded" || currentTeamRole.includes(RECEPTION_MANAGER)) ? true : undefined,
    jobs_to_be_allocated: filter === "jobs_to_be_allocated" ? true : undefined,
    jobs_overdue: filter === "jobs_overdue" ? true : undefined,
    special_jobs_overdue: filter === "special_jobs_overdue" ? true : undefined,
    three_d_jobs_overdue: filter === "three_d_jobs_overdue" ? true : undefined,
    incomplete_jobs: filter === "incomplete_jobs" ? true : undefined,
    unpaid_pay_before_drafting: filter === "unpaid_pay_before_drafting" ? true : undefined,
    collect_payment_onsite: filter === "collect_payment_onsite" ? true : undefined,
    job_docket_to_review: filter === "job_docket_to_review" ? true : undefined,
    jobs_with_drafting_docket: filter === "jobs_with_drafting_docket" ? true : undefined,
    create_deliverables: filter === "create_deliverables" ? true : undefined,
    for_plan_report: filter === "for_plan_report" ? true : undefined,
    resolved_from_rs: filter === "resolved_from_rs" ? true : undefined,
    ready_to_send_for_rs_manager: filter === "ready_to_send_for_rs_manager" || undefined,
    jobs_is_outstanding_invoice: filter === "jobs_is_outstanding_invoice" ? true : undefined,
    job_service_id: job_service_id && filter === "jobs_is_outstanding_invoice" ? job_service_id : undefined,
    searches_to_complete: filter === "searches_to_complete" ? true : undefined,
    can_be_moved_forward: filter === "can_be_moved_forward" ? true : undefined,
    urgent_jobs: filter === "urgent_jobs" ? true : undefined
  }
  const [filters, setFilters] = useState(initialFilters)
  const [isFilterCleared, setIsFilterCleared] = useState(false)
  const [isFilteredByDropDown, setIsFilteredByDropDown] = useState(false)

  const byStatusName = get(LIVE_JOB_STATUS_OPTIONS, `${findIndex(LIVE_JOB_STATUS_OPTIONS, option => option[1] === filters.by_status)}.0`)

  const getDate = val => {
    const momentObj = moment(val)
    if (!momentObj.isValid()) return undefined
    return momentObj.format("YYYY-MM-DD")
  }

  const toDateObject = val => (val ? moment(val, "YYYY-MM-DD").toDate() : null)

  const clearFilters = () => {
    setFilters({
      by_keyword: undefined,
      by_status: undefined,
      field_work_start: undefined,
      field_work_end: undefined,
      due_date_start: undefined,
      due_date_end: undefined,
      job_pack_not_uploaded: undefined
    })
    setIsFilterCleared(true)
  }

  const setBySearch = val => {
    setFilters({ ...filters, by_keyword: val })
  }

  const setByStatus = val => {
    setFilters({ ...filters, by_status: val })
    setIsFilteredByDropDown(true)
  }

  const onRangeChange = (startKey, endKey, dates) => {
    const [start, end] = dates || []
    setFilters({
      ...filters,
      [startKey]: getDate(start),
      [endKey]: getDate(end)
    })
    if ((start && end) || (!start && !end)) {
      setIsFilteredByDropDown(true)
    }
  }

  const jobServiceListHandler = jobList => {
    if (initialFilters.job_pack_not_uploaded) {
      setJobServiceList(orderBy(jobList, [obj => new Date(obj.attributes.bookingDates[0]), "attributes.jobNo"], ["asc", "asc"]))
    } else {
      setJobServiceList(orderBy(jobList, "attributes.bookingTime", "asc"))
    }
  }

  const apiFilters = () => {
    const {
      field_work_start, field_work_end, due_date_start, due_date_end, ...rest
    } = filters
    return {
      ...rest,
      by_field_work_date: (field_work_start && field_work_end) ? [field_work_start, field_work_end] : undefined,
      by_due_date: (due_date_start && due_date_end) ? [due_date_start, due_date_end] : undefined
    }
  }

  const fetchData = () => {
    setRequesting(true)
    getAllJobServices(baseUrl, apiFilters())
      .then(res => {
        if (res.status === 200) {
          const jobs = get(res.response, "jobService", {})
          let jobList = Object.values(jobs)

          const additionalInfo = res.response.meta?.additional_info
          if (additionalInfo && !isEmpty(additionalInfo.meta?.ids)) {
            jobList = additionalInfo.meta.ids.map(id => jobs[id])
            setJobServiceList(jobList)
          } else {
            jobServiceListHandler(jobList);
          }
        }
      })
      .then(() => {
        setRequesting(false)
      })
      .catch(() => {
        setRequesting(false)
      })
  }

  const handleSubmit = e => {
    e.preventDefault()
    fetchData()
  }

  useEffect(() => {
    fetchData()
    setIsFilterCleared(false)
    setIsFilteredByDropDown(false)
    refreshPageTableZoom()
  }, [isFilterCleared, isFilteredByDropDown, currentRole])

  useEffect(() => {
    if (requesting) return undefined;

    const timer = setTimeout(() => refreshPageTableZoom(), 150);
    return () => clearTimeout(timer);
  }, [requesting, jobServiceList])

  return (
    <Container fluid className="">
      <div className="container-inner">
        <div className="d-flex justify-content-between align-items-center header-tabs-container">
          <div className="header-tabs">
            <h1>Live Job Status</h1>
          </div>
        </div>
        <div className="table-listing-filters d-md-flex justify-content-between">
          <div>
            <div className="btn-group mr-2">
              <SearchInput value={filters.by_keyword} setValue={setBySearch} handleSubmit={handleSubmit} dataProcessing={requesting} />
            </div>
            <div className="btn-group mr-2">
              <FilterDropdown title="Status" filteredName={byStatusName} setFilter={setByStatus} options={LIVE_JOB_STATUS_OPTIONS} />
            </div>
            <div className="btn-group mr-2">
              <div className="ca-custom">
                <DatePicker
                  selectsRange
                  startDate={toDateObject(filters.field_work_start)}
                  endDate={toDateObject(filters.field_work_end)}
                  selected={toDateObject(filters.field_work_start)}
                  dateFormat="dd/MM/yyyy"
                  onChange={dates => onRangeChange("field_work_start", "field_work_end", dates)}
                  shouldCloseOnSelect={false}
                  customInput={(
                    <DateFilterButton
                      filterLabel="Field Work"
                      rangeLabel={formatRangeLabel(filters.field_work_start, filters.field_work_end)} />
                  )} />
              </div>
            </div>
            <div className="btn-group mr-2">
              <div className="ca-custom">
                <DatePicker
                  selectsRange
                  startDate={toDateObject(filters.due_date_start)}
                  endDate={toDateObject(filters.due_date_end)}
                  selected={toDateObject(filters.due_date_start)}
                  dateFormat="dd/MM/yyyy"
                  onChange={dates => onRangeChange("due_date_start", "due_date_end", dates)}
                  shouldCloseOnSelect={false}
                  customInput={(
                    <DateFilterButton
                      filterLabel="Due Date"
                      rangeLabel={formatRangeLabel(filters.due_date_start, filters.due_date_end)} />
                  )} />
              </div>
            </div>
            <div className="btn-group">
              <AnchorButton
                className="f-small"
                onClick={clearFilters}>
                Clear Filters
              </AnchorButton>
            </div>
          </div>
        </div>
        {requesting ? <Loader /> : (<JobServicesTable jobServiceList={jobServiceList} fetchData={fetchData} />)}
      </div>
    </Container>
  )
}
